import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { LumeoFile } from 'types/file';
import {
  CursorPaginated,
  CursorPaginationParams,
  OffsetPaginated,
  OffsetPaginationParams,
} from 'types/pagination';
import { TagParams } from 'tags/types/tagged';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

export type UseFilesParams = TagParams & {
  /** ISO 8601 string */
  created_ts_since?: string;
  /** ISO 8601 string */
  created_ts_until?: string;

  camera_ids?: string[];
  deployment_ids?: string[];
  file_ids?: string[];
  gateway_ids?: string[];
  node_ids?: string[];
  pipeline_ids?: string[];
  stream_ids?: string[];

  /** @default false */
  with_thumbnail?: boolean;
  /** @default true */
  include_snapshots?: boolean;
  is_recording?: boolean;
};

type UseFilesOptions = UseQueryOptions<OffsetPaginated<LumeoFile>, APIError> & {
  params?: UseFilesParams & OffsetPaginationParams;
};

export function useFiles({
  queryKey = ['files'],
  params,
  ...options
}: UseFilesOptions = {}) {
  const { applicationID, client } = useAPI();
  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      const { data } = await client.get<OffsetPaginated<LumeoFile>>(
        `/apps/${applicationID}/files`,
        {
          params: queryParams,
        }
      );

      return data;
    },
    options
  );
}

export type UseFilesCursorParams = UseFilesParams & CursorPaginationParams;

type UseFilesCursorOptions = UseQueryOptions<
  CursorPaginated<LumeoFile>,
  APIError
> & {
  params?: UseFilesCursorParams;
};

export function useFilesCursor({
  queryKey = ['files'],
  params,
  ...options
}: UseFilesCursorOptions = {}) {
  const { applicationID, client } = useAPI();
  const queryParams = {
    pagination: 'cursor',
    limit: 50,
    ...params,
  } as const;

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      const { data } = await client.get<CursorPaginated<LumeoFile>>(
        `/apps/${applicationID}/files`,
        {
          params: queryParams,
        }
      );

      return data;
    },
    options
  );
}
